import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  FlaskConical, FileText, Send, Upload, Download, Plus, Trash2,
  Building2, CheckCircle2, Clock, Mail, X, Loader2, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { verbaliPrelievoApi, laboratoriApi, aziendeApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const TIPI_CAMPIONAMENTO = [
  { key: 'tampone_superficie', label: 'TAMPONE DI SUPERFICIE',
    ricerche_default: ['CMT', 'CPT', 'E.coli', 'Salmonella', 'Lysteria M.', 'Enterobacteriacee'] },
  { key: 'tampone_mani', label: 'TAMPONE MANI',
    ricerche_default: ['Staphilococcus A.', 'Salmonella', 'E.coli'] },
  { key: 'acque', label: 'ACQUE',
    ricerche_default: ['Microbiologico completo (conta colonie 36°C/22°C, enterococchi, E.coli, coliformi)', 'Chimico (Al, Fe, Cr, Cu, Pb, durezza, Nitrati, Nitriti)'] },
  { key: 'alimenti_mangimi', label: 'ALIMENTI / MANGIMI',
    ricerche_default: ['E.Coli', 'Salmonella', 'CMT', 'Enterobacteriacee', 'Pseudomonas spp.', 'Stafilococchi coagulasi pos.', 'Listeria monocytogenes'] },
];

const emptyVerbale = {
  azienda_id: '',
  azienda_nome: '',
  azienda_indirizzo: '',
  pdv_nome: '',
  persona_presente: '',
  data_prelievo: new Date().toISOString().slice(0, 10),
  ora_prelievo: '',
  campionamenti: [],
  note: '',
  laboratorio_id: '',
};

export default function VerbaliPrelievoPage() {
  const { user } = useAuth();
  const [verbali, setVerbali] = useState([]);
  const [laboratori, setLaboratori] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [openLabMgr, setOpenLabMgr] = useState(false);
  const [openRefertoUpload, setOpenRefertoUpload] = useState(null);
  const [form, setForm] = useState(emptyVerbale);
  const [newLab, setNewLab] = useState({ nome: '', email: '', telefono: '', indirizzo: '', referente: '', assigned_user_email: '' });
  const [refertoFile, setRefertoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [labelLoadingIdx, setLabelLoadingIdx] = useState(null);  // v80: indice campionamento per cui sta caricando l'etichetta

  // v80: handler per leggere etichetta con AI Vision
  const handleReadLabel = async (idx, file) => {
    if (!file) return;
    setLabelLoadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/verbali-prelievo/read-label`, {
        method: 'POST',
        body: fd,
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      // Popola tutti i campi prodotto del campionamento
      setForm(f => ({
        ...f,
        campionamenti: f.campionamenti.map((c, i) => i === idx ? { ...c, ...data } : c),
      }));
      toast.success(`✓ Etichetta letta: ${data.prodotto_nome || '(senza nome)'}`);
    } catch (e) {
      toast.error(`Errore lettura etichetta: ${e.message || e}`);
    } finally {
      setLabelLoadingIdx(null);
    }
  };

  const role = user?.role || '';
  const isLabUser = role === 'laboratorio_analisi';
  const isAdmin = ['super_admin', 'admin', 'amministratore'].includes(role);
  const isConsulente = ['consulente_haccp', 'haccp_admin', 'haccp_responsabile', 'studio_tecnico', ...['super_admin', 'admin', 'amministratore']].includes(role);

  const load = async () => {
    setLoading(true);
    try {
      const [vr, lr] = await Promise.all([
        verbaliPrelievoApi.list(),
        laboratoriApi.list().catch(() => ({ data: [] })),
      ]);
      setVerbali(vr.data || []);
      setLaboratori(lr.data || []);
      if (isConsulente) {
        aziendeApi.list().then(r => setAziende(r.data || [])).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento verbali');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addCampionamento = (tipoKey) => {
    const tipo = TIPI_CAMPIONAMENTO.find(t => t.key === tipoKey);
    setForm(f => ({
      ...f,
      campionamenti: [...f.campionamenti, {
        tipo: tipoKey,
        descrizione: '',
        ricerche: tipo?.ricerche_default || [],
      }],
    }));
  };
  const removeCampionamento = (idx) => {
    setForm(f => ({ ...f, campionamenti: f.campionamenti.filter((_, i) => i !== idx) }));
  };
  const updateCampionamento = (idx, key, val) => {
    setForm(f => ({
      ...f,
      campionamenti: f.campionamenti.map((c, i) => i === idx ? { ...c, [key]: val } : c),
    }));
  };

  const submitVerbale = async () => {
    if (!form.azienda_nome) { toast.error('Inserisci nome ditta'); return; }
    if (form.campionamenti.length === 0) { toast.error('Aggiungi almeno un campionamento'); return; }
    setSaving(true);
    try {
      await verbaliPrelievoApi.create(form);
      toast.success('Verbale creato');
      setOpenNew(false);
      setForm(emptyVerbale);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore creazione');
    } finally { setSaving(false); }
  };

  const downloadDocx = async (id, numero) => {
    try {
      const r = await verbaliPrelievoApi.generateDocx(id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Verbale_Prelievo_${(numero || id).replace('/', '-')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore download'); }
  };

  const sendToLab = async (id) => {
    try {
      const r = await verbaliPrelievoApi.sendToLab(id);
      if (r.data.sent) toast.success(`Email inviata a ${r.data.lab_email}`);
      else toast.warning(r.data.message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore invio'); }
  };

  const downloadReferto = async (id, numero) => {
    try {
      const r = await verbaliPrelievoApi.downloadReferto(id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Referto_${(numero || id).replace('/', '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Referto non disponibile'); }
  };

  const uploadReferto = async () => {
    if (!refertoFile || !openRefertoUpload) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', refertoFile);
      const resp = await verbaliPrelievoApi.uploadReferto(openRefertoUpload.id, fd);
      // v104 — Mostra esito delle notifiche email
      const n = resp?.data?.notifiche || {};
      const sent = n.sent_to || [];
      const errors = n.errors || [];
      if (sent.length > 0) {
        toast.success(
          `Rapporto inviato! Email di notifica spedite a ${sent.length} destinatari: ${sent.map(s => s.email).join(', ')}`,
          { duration: 8000 }
        );
      } else if (errors.length > 0) {
        toast.success('Rapporto caricato (ma email notifica NON inviate)', { duration: 6000 });
        toast.warning('Notifiche email non inviate: ' + errors.join(' · '), { duration: 10000 });
      } else {
        toast.success('Rapporto di Prova caricato');
      }
      setOpenRefertoUpload(null);
      setRefertoFile(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore upload'); }
    finally { setSaving(false); }
  };

  const deleteVerbale = async (id) => {
    if (!window.confirm('Eliminare il verbale?')) return;
    try {
      await verbaliPrelievoApi.delete(id);
      toast.success('Verbale eliminato');
      load();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  const saveLab = async () => {
    if (!newLab.nome || !newLab.email) { toast.error('Nome ed email obbligatori'); return; }
    try {
      await laboratoriApi.create(newLab);
      toast.success('Laboratorio aggiunto');
      setNewLab({ nome: '', email: '', telefono: '', indirizzo: '', referente: '', assigned_user_email: '' });
      laboratoriApi.list().then(r => setLaboratori(r.data || []));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const deleteLab = async (id) => {
    if (!window.confirm('Eliminare il laboratorio?')) return;
    try {
      await laboratoriApi.delete(id);
      setLaboratori(labs => labs.filter(l => l.id !== id));
    } catch { toast.error('Errore'); }
  };

  // v105 — Upload template Mod.1 personalizzato per il laboratorio
  const uploadMod1Template = async (labId, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Solo file .docx accettati');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande (max 5 MB)');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/laboratori-analisi/${labId}/mod1-template`, {
        method: 'POST',
        body: fd,
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      toast.success('Template Mod.1 caricato');
      laboratoriApi.list().then(rr => setLaboratori(rr.data || []));
    } catch (e) {
      toast.error(`Errore upload template: ${e.message || e}`);
    }
  };

  const deleteMod1Template = async (labId) => {
    if (!window.confirm('Rimuovere il template Mod.1 personalizzato? Verrà usato il template di default.')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/laboratori-analisi/${labId}/mod1-template`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}` },
      });
      toast.success('Template rimosso');
      laboratoriApi.list().then(rr => setLaboratori(rr.data || []));
    } catch { toast.error('Errore'); }
  };

  const downloadMod1Template = async (labId, filename) => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/laboratori-analisi/${labId}/mod1-template`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token') || ''}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'Mod1_template.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore download'); }
  };

  const statusBadge = (s) => {
    if (s === 'creato') return <Badge variant="outline">Creato</Badge>;
    if (s === 'inviato_lab') return <Badge className="bg-blue-100 text-blue-800">Inviato al lab</Badge>;
    if (s === 'referto_ricevuto') return <Badge className="bg-emerald-100 text-emerald-800">Referto ricevuto</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="verbali-prelievo-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FlaskConical className="w-7 h-7 text-emerald-600" /> Verbali di Prelievo
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isLabUser
              ? 'Carica i referti dei verbali a te assegnati.'
              : 'Crea verbali di prelievo, inviali al laboratorio, ricevi i referti.'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(isAdmin || isConsulente) && (
            <Button variant="outline" onClick={() => setOpenLabMgr(true)} data-testid="manage-labs-btn">
              <Building2 className="w-4 h-4 mr-1" /> Laboratori ({laboratori.length})
            </Button>
          )}
          {isConsulente && !isLabUser && (
            <Button onClick={() => { setForm(emptyVerbale); setOpenNew(true); }} data-testid="new-verbale-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuovo Verbale
            </Button>
          )}
        </div>
      </motion.div>

      {loading ? <p>Caricamento...</p> : verbali.length === 0 ? (
        <div className="bg-white p-10 rounded-lg text-center border" data-testid="empty-state">
          <FlaskConical className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">
            {isLabUser ? 'Nessun verbale assegnato.' : 'Nessun verbale ancora creato.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {verbali.map(v => (
            <Card key={v.id} className="hover:shadow-md transition-shadow" data-testid={`verbale-${v.id}`}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-lg font-bold">Verbale n. {v.numero_verbale}</span>
                      {statusBadge(v.status)}
                      <span className="text-xs text-muted-foreground">{v.data_prelievo} {v.ora_prelievo}</span>
                    </div>
                    <p className="text-sm">
                      <strong>{v.azienda_nome}</strong>
                      {v.azienda_indirizzo && <span className="text-muted-foreground"> — {v.azienda_indirizzo}</span>}
                    </p>
                    {/* v108 — UL (sede del prelievo) sempre visibile, evidenziata per il laboratorio */}
                    {v.pdv_nome && (
                      <p className={`text-sm mt-0.5 ${isLabUser ? 'font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded inline-block' : 'text-slate-700'}`}>
                        🏪 Unità Locale: <strong>{v.pdv_nome}</strong>
                        {v.pdv_indirizzo && <span className="font-normal text-slate-600"> — {v.pdv_indirizzo}</span>}
                      </p>
                    )}
                    {/* Mostra il consulente che ha effettuato il prelievo — utile al laboratorio */}
                    {isLabUser && v.created_by_name && (
                      <p className="text-xs text-slate-600 mt-0.5">👤 Consulente: <strong>{v.created_by_name}</strong></p>
                    )}
                    {v.persona_presente && (
                      <p className="text-xs text-muted-foreground">Presente: {v.persona_presente}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(v.campionamenti || []).map((c, i) => {
                        const tipo = TIPI_CAMPIONAMENTO.find(t => t.key === c.tipo);
                        return <Badge key={i} variant="secondary" className="text-[10px]">{tipo?.label || c.tipo}</Badge>;
                      })}
                    </div>
                    {v.note && <p className="text-xs italic text-muted-foreground mt-1">📝 {v.note}</p>}
                  </div>
                  <div className="flex flex-col gap-1 min-w-[200px]">
                    <Button size="sm" variant="outline" onClick={() => downloadDocx(v.id, v.numero_verbale)}
                      data-testid={`download-docx-${v.id}`}>
                      <Download className="w-3 h-3 mr-1" /> Scarica .docx
                    </Button>
                    {isConsulente && !isLabUser && v.laboratorio_id && v.status === 'creato' && (
                      <Button size="sm" onClick={() => sendToLab(v.id)} className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`send-lab-${v.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Invia al laboratorio
                      </Button>
                    )}
                    {(isLabUser || isAdmin) && v.status !== 'referto_ricevuto' && (
                      <Button size="sm" variant="outline" onClick={() => { setOpenRefertoUpload(v); setRefertoFile(null); }}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`upload-referto-${v.id}`}>
                        <Upload className="w-3 h-3 mr-1" /> Invia Rapporto di Prova
                      </Button>
                    )}
                    {(v.referto_pdf_path || v.referto_storage_path) && (
                      <Button size="sm" variant="outline" onClick={() => downloadReferto(v.id, v.numero_verbale)}
                        className="border-emerald-300 text-emerald-700"
                        data-testid={`download-referto-${v.id}`}>
                        <FileText className="w-3 h-3 mr-1" /> Rapporto di Prova
                      </Button>
                    )}
                    {(isAdmin || v.created_by === user?.id) && !isLabUser && (
                      <Button size="sm" variant="ghost" onClick={() => deleteVerbale(v.id)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`delete-${v.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Elimina
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* DIALOG: Nuovo Verbale */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Verbale di Prelievo</DialogTitle>
            <DialogDescription>Compila i dati del prelievo, scegli i campionamenti e il laboratorio destinatario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data prelievo</Label>
                <Input type="date" value={form.data_prelievo} onChange={e => setForm({...form, data_prelievo: e.target.value})} data-testid="form-data" />
              </div>
              <div>
                <Label>Ora prelievo</Label>
                <Input type="time" value={form.ora_prelievo} onChange={e => setForm({...form, ora_prelievo: e.target.value})} data-testid="form-ora" />
              </div>
            </div>
            <div>
              <Label>Ditta / Azienda *</Label>
              <Input value={form.azienda_nome} onChange={e => setForm({...form, azienda_nome: e.target.value})}
                placeholder="Es. Pasticceria Mario SRL"
                list="aziende-list" data-testid="form-azienda" />
              <datalist id="aziende-list">
                {aziende.map(a => <option key={a.id} value={a.denominazione || a.ragione_sociale} />)}
              </datalist>
            </div>
            <div>
              <Label>Indirizzo ditta</Label>
              <Input value={form.azienda_indirizzo} onChange={e => setForm({...form, azienda_indirizzo: e.target.value})}
                placeholder="Es. Via Roma 10, Catania" data-testid="form-indirizzo" />
            </div>
            <div>
              <Label>Persona presente (OSA / responsabile)</Label>
              <Input value={form.persona_presente} onChange={e => setForm({...form, persona_presente: e.target.value})}
                placeholder="Es. Mario Rossi (gerente)" data-testid="form-persona" />
            </div>

            {/* Campionamenti */}
            <div>
              <Label>Campionamenti effettuati *</Label>
              <p className="text-xs text-muted-foreground mb-1">Puoi aggiungere più campionamenti anche dello stesso tipo (es. 3 tamponi di superficie in punti diversi).</p>
              <div className="flex flex-wrap gap-2 mb-2 mt-1">
                {TIPI_CAMPIONAMENTO.map(t => {
                  const count = form.campionamenti.filter(c => c.tipo === t.key).length;
                  return (
                    <Button key={t.key} type="button" size="sm" variant="outline"
                      onClick={() => addCampionamento(t.key)}
                      data-testid={`add-camp-${t.key}`}>
                      <Plus className="w-3 h-3 mr-1" /> {t.label}{count > 0 ? ` (${count})` : ''}
                    </Button>
                  );
                })}
              </div>
              <div className="space-y-2">
                {form.campionamenti.map((c, idx) => {
                  const tipo = TIPI_CAMPIONAMENTO.find(t => t.key === c.tipo);
                  const showProdotto = c.tipo === 'alimenti_mangimi' || c.prodotto_nome;
                  return (
                    <div key={idx} className="border rounded p-3 bg-slate-50">
                      <div className="flex justify-between items-center mb-2">
                        <strong className="text-sm">{tipo?.label} — n. {idx + 1}</strong>
                        <Button size="sm" variant="ghost" onClick={() => removeCampionamento(idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <Input value={c.descrizione} onChange={e => updateCampionamento(idx, 'descrizione', e.target.value)}
                        placeholder="Punto/luogo del prelievo (es. piano di lavoro)"
                        className="mb-2 text-sm" />

                      {/* v80: AI Leggi Etichetta — appare per alimenti_mangimi o sempre disponibile */}
                      <div className="mb-2 flex items-center gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-1 cursor-pointer text-xs bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 px-2 py-1 rounded">
                          <Sparkles className="w-3 h-3" />
                          {labelLoadingIdx === idx ? 'Lettura AI...' : 'Leggi etichetta con AI'}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            disabled={labelLoadingIdx !== null}
                            onChange={(e) => handleReadLabel(idx, e.target.files?.[0])}
                            data-testid={`read-label-${idx}`}
                          />
                        </label>
                        {showProdotto && (
                          <span className="text-xs text-emerald-700 italic">
                            ✓ {c.prodotto_nome || 'Compila i dati prodotto manualmente o leggi l\'etichetta'}
                          </span>
                        )}
                      </div>

                      {/* Campi prodotto (visibili per alimenti o se popolati dall'AI) */}
                      {showProdotto && (
                        <div className="grid grid-cols-2 gap-2 mb-2 p-2 bg-white border border-emerald-200 rounded">
                          <Input value={c.prodotto_nome || ''} onChange={e => updateCampionamento(idx, 'prodotto_nome', e.target.value)}
                            placeholder="Nome prodotto" className="text-xs" data-testid={`prod-nome-${idx}`} />
                          <Input value={c.prodotto_marchio || ''} onChange={e => updateCampionamento(idx, 'prodotto_marchio', e.target.value)}
                            placeholder="Marchio" className="text-xs" />
                          <Input value={c.prodotto_lotto || ''} onChange={e => updateCampionamento(idx, 'prodotto_lotto', e.target.value)}
                            placeholder="Lotto" className="text-xs" data-testid={`prod-lotto-${idx}`} />
                          <Input value={c.prodotto_tmc || ''} onChange={e => updateCampionamento(idx, 'prodotto_tmc', e.target.value)}
                            placeholder="TMC / Scadenza" className="text-xs" data-testid={`prod-tmc-${idx}`} />
                          <Input value={c.prodotto_peso || ''} onChange={e => updateCampionamento(idx, 'prodotto_peso', e.target.value)}
                            placeholder="Peso netto" className="text-xs" />
                          <Input value={c.prodotto_ean || ''} onChange={e => updateCampionamento(idx, 'prodotto_ean', e.target.value)}
                            placeholder="EAN/GTIN" className="text-xs" />
                          <Input value={c.prodotto_produttore || ''} onChange={e => updateCampionamento(idx, 'prodotto_produttore', e.target.value)}
                            placeholder="Produttore" className="text-xs col-span-2" />
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1">
                        {(tipo?.ricerche_default || []).map(r => (
                          <label key={r} className="text-xs flex items-center gap-1 cursor-pointer">
                            <Checkbox
                              checked={c.ricerche?.includes(r)}
                              onCheckedChange={(checked) => {
                                const newRic = checked
                                  ? [...(c.ricerche || []), r]
                                  : (c.ricerche || []).filter(x => x !== r);
                                updateCampionamento(idx, 'ricerche', newRic);
                              }}
                            />
                            {r}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Note sul campione</Label>
              <Textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})}
                placeholder="Annotazioni eventuali..." rows={2} data-testid="form-note" />
            </div>

            <div>
              <Label>Laboratorio destinatario</Label>
              <select value={form.laboratorio_id} onChange={e => setForm({...form, laboratorio_id: e.target.value})}
                className="w-full border rounded p-2 text-sm" data-testid="form-lab">
                <option value="">— Seleziona laboratorio —</option>
                {laboratori.map(l => (
                  <option key={l.id} value={l.id}>{l.nome} ({l.email})</option>
                ))}
              </select>
              {laboratori.length === 0 && isAdmin && (
                <p className="text-xs text-amber-600 mt-1">⚠ Nessun laboratorio. Vai su &quot;Laboratori&quot; per aggiungerne.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annulla</Button>
            <Button onClick={submitVerbale} disabled={saving} data-testid="submit-verbale">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Crea Verbale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Gestione Laboratori */}
      <Dialog open={openLabMgr} onOpenChange={setOpenLabMgr}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Laboratori di Analisi</DialogTitle>
            <DialogDescription>Gestisci i laboratori a cui inviare i verbali.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border rounded p-3 space-y-2 bg-slate-50">
              <h4 className="font-medium text-sm">Aggiungi nuovo</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Nome lab *" value={newLab.nome} onChange={e => setNewLab({...newLab, nome: e.target.value})} data-testid="new-lab-nome" />
                <Input placeholder="Email *" type="email" value={newLab.email} onChange={e => setNewLab({...newLab, email: e.target.value})} data-testid="new-lab-email" />
                <Input placeholder="Telefono" value={newLab.telefono} onChange={e => setNewLab({...newLab, telefono: e.target.value})} />
                <Input placeholder="Referente" value={newLab.referente} onChange={e => setNewLab({...newLab, referente: e.target.value})} />
                <Input placeholder="Indirizzo" value={newLab.indirizzo} onChange={e => setNewLab({...newLab, indirizzo: e.target.value})} className="col-span-2" />
                <Input
                  placeholder="Email collaboratore/consulente assegnato (chi gestirà il lab)"
                  type="email"
                  value={newLab.assigned_user_email}
                  onChange={e => setNewLab({...newLab, assigned_user_email: e.target.value})}
                  className="col-span-2 bg-amber-50 border-amber-300"
                  data-testid="new-lab-assigned-email"
                />
              </div>
              <p className="text-[11px] text-amber-800 italic">L'utente con questa email vedrà i verbali destinati a questo lab e potrà caricare i rapporti di prova.</p>
              <Button size="sm" onClick={saveLab} data-testid="save-lab"><Plus className="w-3 h-3 mr-1"/> Aggiungi</Button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {laboratori.map(l => (
                <div key={l.id} className="p-2 border rounded hover:bg-slate-50 space-y-2">
                  <div className="flex justify-between items-start">
                    <div className="text-sm flex-1">
                      <div className="font-medium">{l.nome}</div>
                      <div className="text-xs text-muted-foreground">{l.email} · {l.telefono}</div>
                      {l.assigned_user_email && (
                        <div className="text-[11px] text-amber-700 mt-0.5">👤 Assegnato a: <strong>{l.assigned_user_email}</strong></div>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteLab(l.id)} className="text-red-600">
                      <Trash2 className="w-3 h-3"/>
                    </Button>
                  </div>

                  {/* v105 — Template Mod.1 personalizzato */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-2">
                    <div className="text-[11px] font-medium text-indigo-900 mb-1">📄 Template Mod.1 Accettazione personalizzato</div>
                    {l.mod1_template_path ? (
                      <div className="flex items-center gap-2 text-xs">
                        <FileText className="w-3 h-3 text-indigo-700"/>
                        <span className="text-indigo-800 flex-1 truncate" title={l.mod1_template_filename}>
                          {l.mod1_template_filename || 'template.docx'}
                        </span>
                        <Button size="sm" variant="ghost" className="h-6 text-indigo-700" onClick={() => downloadMod1Template(l.id, l.mod1_template_filename)} data-testid={`lab-tpl-dl-${l.id}`}>
                          <Download className="w-3 h-3"/>
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 text-red-600" onClick={() => deleteMod1Template(l.id)} data-testid={`lab-tpl-del-${l.id}`}>
                          <X className="w-3 h-3"/>
                        </Button>
                      </div>
                    ) : (
                      <p className="text-[11px] text-indigo-700 italic">Nessun template caricato — verrà usato quello di default</p>
                    )}
                    <label className="block mt-1">
                      <input
                        type="file"
                        accept=".docx"
                        className="hidden"
                        onChange={(e) => uploadMod1Template(l.id, e.target.files?.[0])}
                        data-testid={`lab-tpl-up-${l.id}`}
                      />
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white text-[11px] rounded cursor-pointer hover:bg-indigo-700">
                        <Upload className="w-3 h-3"/>
                        {l.mod1_template_path ? 'Sostituisci template' : 'Carica template .docx'}
                      </span>
                    </label>
                    <p className="text-[10px] text-indigo-600 mt-1">
                      Placeholder supportati: <code>{`{{numero_verbale}}`}</code>, <code>{`{{data_accettazione}}`}</code>, <code>{`{{azienda_nome}}`}</code>, <code>{`{{lab_nome}}`}</code>, <code>{`{% for c in campioni %}`}</code>, <code>{`{{firma_lab}}`}</code>…
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Upload Referto */}
      <Dialog open={!!openRefertoUpload} onOpenChange={(o) => { if (!o) setOpenRefertoUpload(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Rapporto di Prova</DialogTitle>
            <DialogDescription>
              Verbale n. {openRefertoUpload?.numero_verbale} — {openRefertoUpload?.azienda_nome}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>File PDF del Rapporto di Prova</Label>
            <Input type="file" accept=".pdf" onChange={e => setRefertoFile(e.target.files?.[0])} data-testid="referto-file-input" />
            {refertoFile && (
              <p className="text-xs text-muted-foreground">
                File: <strong>{refertoFile.name}</strong> · {(refertoFile.size / 1024).toFixed(1)} KB
              </p>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
              <p className="font-medium text-blue-900">📧 Notifica automatica</p>
              <p className="text-blue-800 mt-0.5">
                Al click di "Invia" verranno spedite email di notifica all'<strong>Azienda</strong>, all'<strong>Unità Locale</strong> e al <strong>Consulente HACCP</strong> che ha effettuato il prelievo.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRefertoUpload(null)}>Annulla</Button>
            <Button onClick={uploadReferto} disabled={!refertoFile || saving} data-testid="submit-referto">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Upload className="w-4 h-4 mr-1"/>}
              Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
